import React, { useEffect, useState } from "react";
import { Phone, ArrowRight, Sparkles } from "lucide-react";
import { BUSINESS, HERO_SLIDES } from "@/lib/constants";

const INTERVAL_MS = 4000;

export default function Hero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Preload all slides so crossfades are instant.
    HERO_SLIDES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#0A1228]"
    >
      {/* Slideshow layer */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((src, i) => (
          <div
            key={src}
            data-testid={`hero-slide-${i}`}
            className="absolute inset-0 transition-opacity duration-[1600ms] ease-in-out"
            style={{
              opacity: i === index ? 1 : 0,
              backgroundImage: `url(${src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              transform: i === index ? "scale(1.04)" : "scale(1)",
              transitionProperty: "opacity, transform",
              transitionDuration: "1600ms, 6000ms",
            }}
            aria-hidden="true"
          />
        ))}
        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1228]/70 via-[#0A1228]/55 to-[#0A1228]/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1228]/80 via-transparent to-transparent" />
      </div>

      {/* Top corner brand mark */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-24 md:pb-40">
        <div className="max-w-3xl">
          <div className="overline mb-7 flex items-center text-[#D4B47A]">
            <span className="inline-block w-10 h-px bg-[#D4B47A] mr-4" />
            KL Kárpitos · Monor
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif-display text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight text-[#F5F2EC]"
          >
            Minőségi kárpitos munka,{" "}
            <span className="italic text-[#D4B47A] font-light">
              hosszú élettartamra
            </span>{" "}
            tervezve.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-8 text-base md:text-lg text-[#F5F2EC]/85 leading-relaxed max-w-xl font-light"
          >
            Bútor kárpitozás, egyedi elképzelések megvalósítása és kültéri
            párnázat — háztól-házig szállítással, garanciával.
          </p>

          <div className="mt-11 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <button
              onClick={() => scrollTo("ajanlat")}
              data-testid="hero-cta-quote"
              className="group relative inline-flex items-center justify-center gap-3 bg-[#D4B47A] text-[#0A1228] px-8 md:px-10 py-5 text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.25em] font-medium hover:bg-[#E5C58B] transition-all duration-300 shadow-[0_8px_30px_rgba(212,180,122,0.35)] hover:shadow-[0_12px_40px_rgba(212,180,122,0.55)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              <Sparkles size={18} className="text-[#0A1228]" />
              Ingyenes árajánlatot kérek
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1.5"
              />
            </button>
            <a
              href={`tel:${BUSINESS.phoneTel}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-2 text-[#F5F2EC] hover:text-[#D4B47A] transition-colors px-2 py-2 text-xs uppercase tracking-[0.25em] border-b border-[#F5F2EC]/30 hover:border-[#D4B47A]"
            >
              <Phone size={14} /> {BUSINESS.phoneDisplay}
            </a>
          </div>
        </div>

        {/* Slide indicators */}
        <div
          className="mt-16 md:mt-24 flex items-center gap-3"
          data-testid="hero-indicators"
        >
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`hero-indicator-${i}`}
              aria-label={`${i + 1}. kép`}
              className="group relative h-px w-10 md:w-14 overflow-hidden bg-[#F5F2EC]/25"
            >
              <span
                className="absolute inset-y-0 left-0 bg-[#D4B47A] transition-all duration-500"
                style={{ width: i === index ? "100%" : "0%" }}
              />
            </button>
          ))}
          <span className="ml-3 text-[10px] tracking-[0.3em] uppercase text-[#F5F2EC]/60 tabular-nums">
            {String(index + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
